import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import "moment-timezone";

const API_BASE_URL = process.env.REACT_APP_BACKURL || "http://localhost:8000";
const DEFAULT_ADDRESS_TYPE_ID = 2;

const initialFilterCriteria = {
	title: "",
	orgID: "",
	startDate: "",
	endDate: "",
	city: "",
	state: "",
	locationName: "",
};

const initialCreateFormData = {
	title: "",
	description: "",
	effectiveFrom: "",
	effectiveTo: "",
	startTime: "09:00:00",
	endTime: "17:00:00",
	timeZone: "America/Chicago",
	bulletinBoardPushDate: "",
	imageFlyer: "",
	locationName: "",
	addressLine1: "",
	city: "",
	state: "",
	zip: "",
	hostingOrgID: "",
};

export const useEventsData = () => {
	// State for events and loading
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// State for organizations and filters
	const [organizations, setOrganizations] = useState([]);
	const [filterCriteria, setFilterCriteria] = useState(initialFilterCriteria);
	const [openFilter, setOpenFilter] = useState(false);
	const [isFiltered, setIsFiltered] = useState(false);

	// State for calendar control
	const [currentView, setCurrentView] = useState("month");
	const [currentDate, setCurrentDate] = useState(new Date());

	// State for Event Detail/Edit Modal
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [detailedEventData, setDetailedEventData] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);

	// State for editing form
	const [editFormData, setEditFormData] = useState({});
	const [updateError, setUpdateError] = useState(null);
	const [updateLoading, setUpdateLoading] = useState(false);

	// States for creation modal
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [createFormData, setCreateFormData] = useState(initialCreateFormData);
	const [createError, setCreateError] = useState(null);
	const [createLoading, setCreateLoading] = useState(false);

	// State for user authorization
	const [userRole, setUserRole] = useState(null);
	const [userOrgID, setUserOrgID] = useState(null);
	const [authLoading, setAuthLoading] = useState(true);

	// Delete confirmation state
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState(null);

	// --- UTILITY FUNCTIONS ---

	const getImageUrl = useCallback((imageFlyer) => {
		if (
			imageFlyer &&
			imageFlyer.type === "Buffer" &&
			imageFlyer.data &&
			imageFlyer.data.length > 0
		) {
			try {
				return String.fromCharCode.apply(null, imageFlyer.data);
			} catch (e) {
				console.error("Error decoding imageFlyer buffer:", e);
				return null;
			}
		}
		return null;
	}, []);

	const buildQueryString = useCallback((criteria, isFilterSearch) => {
		const params = new URLSearchParams();

		if (criteria.orgID) {
			params.append("orgID", criteria.orgID);
		}

		for (const key in criteria) {
			if (
				key !== "orgID" &&
				key !== "startDate" &&
				key !== "endDate" &&
				criteria[key]
			) {
				params.append(key, criteria[key]);
			}
		}

		if (criteria.startDate) {
			params.append("startDate", criteria.startDate);
		}
		if (criteria.endDate) {
			params.append("endDate", criteria.endDate);
		}

		return params.toString();
	}, []);

	const canEditEvent = useCallback((role, userOrgId, eventData) => {
		if (!eventData || !eventData.hostingOrgID) return false;

		if (role === "0") {
			return true;
		}

		if (role === "1") {
			return userOrgId === String(eventData.hostingOrgID);
		}

		return false;
	}, []);

	const canCreateEvent = useCallback((role) => {
		return role === "0" || role === "1";
	}, []);

	// --- API FUNCTIONS ---

	const fetchEvents = useCallback(
		async (criteria, isFilterSearch) => {
			setLoading(true);
			setError(null);
			setEvents([]);

			const queryString = buildQueryString(criteria, isFilterSearch);

			try {
				const response = await axios.get(
					`${API_BASE_URL}/api/foodpantry/event?${queryString}`,
					{
						withCredentials: true,
					},
				);

				const formattedEvents = response.data.map((event) => {
					let startDate = new Date(event.effectiveFrom);
					let endDate = new Date(event.effectiveTo);

					if (event.startTime && event.endTime) {
						const dateFrom = moment
							.utc(event.effectiveFrom)
							.format("YYYY-MM-DD");
						const dateTo = moment.utc(event.effectiveTo).format("YYYY-MM-DD");
						const timeStart = event.startTime;
						const timeEnd = event.endTime;
						const eventTimeZone = event.timeZone || "UTC";

						const combinedStart = `${dateFrom} ${timeStart}`;
						const combinedEnd = `${dateTo} ${timeEnd}`;

						startDate = moment
							.tz(combinedStart, "YYYY-MM-DD HH:mm:ss", eventTimeZone)
							.toDate();
						endDate = moment
							.tz(combinedEnd, "YYYY-MM-DD HH:mm:ss", eventTimeZone)
							.toDate();
					}

					return {
						...event,
						id: event.eventID,
						title: event.title,
						start: startDate,
						end: endDate,
						allDay: false,
					};
				});

				setEvents(formattedEvents);
				setIsFiltered(isFilterSearch);
			} catch (err) {
				console.error(
					"Failed to fetch events. Details:",
					err.response ? err.response.data : err.message,
				);
				setError(
					"Could not load calendar events. Please check the console for details, or try reloading the page.",
				);
			} finally {
				setLoading(false);
			}
		},
		[buildQueryString],
	);

	const fetchEventDetails = useCallback(async (eventId) => {
		setDetailLoading(true);
		setError(null);
		setDetailedEventData(null);

		try {
			const response = await axios.get(`${API_BASE_URL}/api/foodpantry/event/${eventId}`, {
				withCredentials: true,
			});

			setDetailedEventData(response.data);
		} catch (err) {
			console.error("Failed to fetch event details:", err);
			setError("Could not load detailed event information.");
		} finally {
			setDetailLoading(false);
		}
	}, []);

	const fetchOrganizations = useCallback(async () => {
		try {
			const url = `${API_BASE_URL}/api/foodPantry/organization`;
			const response = await fetch(url, {
				method: "GET",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
			});

			if (!response.ok) {
				const text = await response.text();
				throw new Error(
					`Failed to fetch organizations: Status ${response.status}. Message: ${text}`,
				);
			}

			const data = await response.json();
			setOrganizations(data.OrgNames || []);
		} catch (err) {
			console.error("Error fetching organizations for filter:", err);
		}
	}, []);

	const fetchUserAuthInfo = useCallback(async () => {
		const sessionID = Cookies.get("sessionID");
		const orgIDFromCookie = Cookies.get("OrgID");

		if (!sessionID) {
			setAuthLoading(false);
			return;
		}

		try {
			const roleUrl = `${API_BASE_URL}/api/foodPantry/user/role`;
			const response = await axios.get(roleUrl, { withCredentials: true });
			const role = response.data.roleID;

			setUserRole(role);
			setUserOrgID(orgIDFromCookie || null);

			if (role === "1" && orgIDFromCookie) {
				setCreateFormData((prev) => ({
					...prev,
					hostingOrgID: orgIDFromCookie,
				}));
			} else if (role === "0") {
				setCreateFormData((prev) => ({ ...prev, hostingOrgID: "" }));
			}
		} catch (error) {
			console.error("Error fetching user role for calendar access:", error);
			setUserRole(null);
			setUserOrgID(null);
		} finally {
			setAuthLoading(false);
		}
	}, []);

	// --- EVENT HANDLERS ---

	const handleFilterChange = (e) => {
		const { name, value } = e.target;
		setFilterCriteria((prev) => ({ ...prev, [name]: value }));
	};

	const handleApplyFilters = (e) => {
		e.preventDefault();
		fetchEvents(filterCriteria, true);
		setOpenFilter(false);
	};

	const handleClearFilters = () => {
		setFilterCriteria(initialFilterCriteria);
		fetchEvents(initialFilterCriteria, false);
		setOpenFilter(false);
	};

	// Create Modal Handlers
	const handleShowCreateModal = () => {
		if (userRole === "1" && userOrgID) {
			setCreateFormData((prev) => ({
				...initialCreateFormData,
				hostingOrgID: userOrgID,
			}));
		} else {
			setCreateFormData(initialCreateFormData);
		}
		setCreateError(null);
		setShowCreateModal(true);
	};

	const handleCloseCreateModal = () => {
		setShowCreateModal(false);
		setCreateError(null);
		setCreateLoading(false);
	};

	const handleCreateFormChange = (e) => {
		const { name, value } = e.target;
		if (name === "cancel") {
			handleCloseCreateModal();
			return;
		}
		setCreateFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCreateSubmit = async (e) => {
		e.preventDefault();
		setCreateLoading(true);
		setCreateError(null);

		const targetOrgID = createFormData.hostingOrgID;
		if (!targetOrgID) {
			setCreateError("Hosting Organization must be selected.");
			setCreateLoading(false);
			return;
		}

		const payload = {
			event: {
				title: createFormData.title,
				description: createFormData.description,
				bulletinBoardPushDate: createFormData.bulletinBoardPushDate || null,
				imageFlyer: createFormData.imageFlyer || null,
				serviceTypeIDs: Array.isArray(createFormData.serviceTypeIDs)
					? createFormData.serviceTypeIDs
					: [],
			},
			schedule: {
				effectiveFrom: createFormData.effectiveFrom,
				effectiveTo: createFormData.effectiveTo,
				specificDate: createFormData.effectiveFrom,
				name: "Monday",
				startTime: createFormData.startTime,
				endTime: createFormData.endTime,
				timeZone: createFormData.timeZone,
			},
			address: {
				addressLine1: createFormData.addressLine1,
				addressLine2: null,
				//county: null,
				city: createFormData.city,
				state: createFormData.state,
				zip: createFormData.zip,
				addressTypeID: DEFAULT_ADDRESS_TYPE_ID,
				locationName: createFormData.locationName,
			},
		};

		try {
			await axios.post(`${API_BASE_URL}/api/foodpantry/event/${targetOrgID}`, payload, {
				withCredentials: true,
			});

			handleCloseCreateModal();
			fetchEvents(filterCriteria, isFiltered);
		} catch (err) {
			console.error(
				"Failed to create event:",
				err.response ? err.response.data : err.message,
			);
			setCreateError(
				`Failed to create event. Details: ${err.response?.data?.message || err.message}`,
			);
		} finally {
			setCreateLoading(false);
		}
	};

	// Detail Modal Handlers
	const handleSelectEvent = (event) => {
		setSelectedEvent(event);
		fetchEventDetails(event.id);
		setShowDetailModal(true);
		setIsEditing(false);
		setUpdateError(null);
	};

	const handleCloseDetailModal = () => {
		setShowDetailModal(false);
		setSelectedEvent(null);
		setDetailedEventData(null);
		setError(null);
		setUpdateError(null);
		setIsEditing(false);
	};

	const handleEditEvent = () => {
		if (!detailedEventData) return;

		const schedule = detailedEventData.schedule?.[0] || {};
		const address = detailedEventData.addresses?.[0] || {};

		const effectiveFromDate = schedule.effectiveFrom
			? moment.utc(schedule.effectiveFrom).format("YYYY-MM-DD")
			: "";
		const effectiveToDate = schedule.effectiveTo
			? moment.utc(schedule.effectiveTo).format("YYYY-MM-DD")
			: "";
		const bulletinBoardPushDate = detailedEventData.bulletinBoardPushDate
			? moment.utc(detailedEventData.bulletinBoardPushDate).format("YYYY-MM-DD")
			: "";
		const imageFlyerUrl = getImageUrl(detailedEventData.imageFlyer) || "";

		// Extract the service type IDs from the event's existing services
		const selectedServiceTypeIDs = Array.isArray(detailedEventData.serviceTypes)
			? detailedEventData.serviceTypes.map((service) => String(service.serviceTypeID))
			: [];

		setEditFormData({
			title: detailedEventData.title || "",
			description: detailedEventData.description || "",
			effectiveFrom: effectiveFromDate,
			effectiveTo: effectiveToDate,
			startTime: schedule.startTime || "",
			endTime: schedule.endTime || "",
			timeZone: schedule.timeZone || "America/Chicago",
			bulletinBoardPushDate: bulletinBoardPushDate,
			imageFlyer: imageFlyerUrl || "",
			locationName: address.locationName || "",
			addressLine1: address.addressLine1 || "",
			city: address.city || "",
			state: address.state || "",
			zip: address.zip || "",
			serviceTypeIDs: selectedServiceTypeIDs
		});

		setIsEditing(true);
		setUpdateError(null);
	};

	const [serviceTypes, setServiceTypes] = useState([]);
	const [serviceTypesError, setServiceTypesError] = useState("");
	const [serviceTypesLoading, setServiceTypesLoading] = useState(false);

	useEffect(() => {
		if (!showCreateModal) return;

		(async () => {
			try {
				setServiceTypesLoading(true);
				setServiceTypesError("");
				const res = await axios.get(
					`${API_BASE_URL}/api/foodPantry/event/serviceTypes`,
					{ withCredentials: true }
				);
				console.log("Service Types API Response:", res);
				console.log("Service Types Data:", res.data);
				setServiceTypes(res.data || []);
			} catch (e) {
				console.error("Failed to load service types:", e);
				console.error("Error response:", e.response);
				setServiceTypes([]);
				setServiceTypesError("Failed to load service types.");
			} finally {
				setServiceTypesLoading(false);
			}
		})();
	}, [showCreateModal]);

	useEffect(() => {
		if (!showDetailModal) return;

		(async () => {
			try {
				setServiceTypesLoading(true);
				setServiceTypesError("");
				const res = await axios.get(
					`${API_BASE_URL}/api/foodPantry/event/serviceTypes`,
					{ withCredentials: true }
				);
				console.log("Service Types API Response:", res);
				console.log("Service Types Data:", res.data);
				setServiceTypes(res.data || []);
			} catch (e) {
				console.error("Failed to load service types:", e);
				console.error("Error response:", e.response);
				setServiceTypes([]);
				setServiceTypesError("Failed to load service types.");
			} finally {
				setServiceTypesLoading(false);
			}
		})();
	}, [showDetailModal]);
	
	const handleEditFormChange = (e) => {
		const { name, value } = e.target;
		setEditFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleUpdateSubmit = async (e) => {
		e.preventDefault();
		setUpdateLoading(true);
		setUpdateError(null);

		if (!selectedEvent?.id) {
			setUpdateError("Event ID missing. Cannot update.");
			setUpdateLoading(false);
			return;
		}

		const orgID =
			detailedEventData?.hostingOrgID ||
			selectedEvent?.hostingOrgID ||
			Cookies.get("OrgID");

		if (!orgID) {
			setUpdateError("Organization ID missing. Cannot update.");
			setUpdateLoading(false);
			return;
		}

		const schedule0 = detailedEventData?.schedule?.[0] || {};

		const payloadEvent = {
			title: editFormData.title,
			description: editFormData.description,
			effectiveFrom: editFormData.effectiveFrom,
			effectiveTo: editFormData.effectiveTo,
			startTime: editFormData.startTime,
			endTime: editFormData.endTime,
			timeZone: editFormData.timeZone,

			// include these if your backend day-update should run
			name: schedule0.name || "Specific Date",
			specificDate: schedule0.specificDate || editFormData.effectiveFrom,

			locationName: editFormData.locationName,
			addressLine1: editFormData.addressLine1,
			addressLine2: null, // add input later if needed
			city: editFormData.city,
			state: editFormData.state,
			zip: editFormData.zip,
			addressTypeID:
			detailedEventData?.addresses?.[0]?.addressTypeID || DEFAULT_ADDRESS_TYPE_ID,

			bulletinBoardPushDate: editFormData.bulletinBoardPushDate || null,
			imageFlyer: editFormData.imageFlyer || null,
			serviceTypeIDs: editFormData.serviceTypeIDs || [],
		};

		try {
			await axios.put(
			`${API_BASE_URL}/api/foodPantry/event/${selectedEvent.id}/organizations/${orgID}`,
			{ event: payloadEvent }, // <-- required wrapper
			{ withCredentials: true }
			);

			handleCloseDetailModal();
			fetchEvents(filterCriteria, isFiltered);
		} catch (err) {
			console.error("Failed to update event:", err.response ? err.response.data : err.message);
			setUpdateError(
			`Failed to update event. Details: ${err.response?.data?.message || err.message}`
			);
		} finally {
			setUpdateLoading(false);
		}
	};


	// Delete handlers
	const handleShowDeleteConfirm = () => {
		setDeleteError(null);
		setShowDeleteConfirm(true);
	};

	const handleCloseDeleteConfirm = () => {
		setShowDeleteConfirm(false);
		setDeleteError(null);
	};

	const handleDeleteSubmit = async () => {
		if (!detailedEventData) return;

		setDeleteLoading(true);
		setDeleteError(null);

		const eventID = detailedEventData.eventID;
		const hostingOrgID = detailedEventData.hostingOrgID;

		try {
			const deleteUrl = `${API_BASE_URL}/api/foodpantry/event`;
			const payload = {
				event: {
					eventID: eventID,
					hostingOrgID: hostingOrgID,
				},
			};

			await axios.delete(deleteUrl, {
				data: payload,
				withCredentials: true,
			});

			setShowDeleteConfirm(false);
			handleCloseDetailModal();
			fetchEvents(filterCriteria, isFiltered);
		} catch (err) {
			console.error(
				"Failed to delete event:",
				err.response ? err.response.data : err.message,
			);
			setDeleteError(
				`Failed to delete event. Details: ${err.response?.data?.message || err.message}`,
			);
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleSelectSlot = useCallback(({ start }) => {
		setCurrentView("day");
		setCurrentDate(start);
	}, []);

	// --- EFFECTS ---

	useEffect(() => {
		fetchOrganizations();
	}, [fetchOrganizations]);

	useEffect(() => {
		fetchUserAuthInfo();
	}, [fetchUserAuthInfo]);

	useEffect(() => {
		fetchEvents(initialFilterCriteria, false);
	}, [fetchEvents]);

	// --- RETURN ALL STATE AND HANDLERS ---
	return {
		// State
		events,
		loading,
		error,
		organizations,
		filterCriteria,
		openFilter,
		setOpenFilter,
		isFiltered,
		currentView,
		setCurrentView,
		currentDate,
		setCurrentDate,
		showDetailModal,
		isEditing,
		setIsEditing,
		selectedEvent,
		detailedEventData,
		detailLoading,
		editFormData,
		updateError,
		updateLoading,
		showCreateModal,
		createFormData,
		createError,
		createLoading,
		userRole,
		userOrgID,
		authLoading,
		showDeleteConfirm,
		deleteLoading,
		deleteError,
		serviceTypes,
  		serviceTypesLoading,
  		serviceTypesError,
		// Functions
		getImageUrl,
		canEditEvent,
		canCreateEvent,
		handleFilterChange,
		handleApplyFilters,
		handleClearFilters,
		handleShowCreateModal,
		handleCloseCreateModal,
		handleCreateFormChange,
		handleCreateSubmit,
		handleSelectEvent,
		handleCloseDetailModal,
		handleEditEvent,
		handleEditFormChange,
		handleUpdateSubmit,
		handleShowDeleteConfirm,
		handleCloseDeleteConfirm,
		handleDeleteSubmit,
		handleSelectSlot,
	};
};

export { API_BASE_URL, DEFAULT_ADDRESS_TYPE_ID, initialFilterCriteria, initialCreateFormData };
